import { OrderStatus, OrderScope } from '@/redux/back-dashboard-api';

type CatQuery = 'active' | 'preparation' | 'return';
type UserTypeQuery = 'users' | 'administrators' | 'externalUsers';

export const buildUrl = (pathname: string, params?: URLSearchParams | any) => {
  const search =
    params instanceof URLSearchParams
      ? params.toString()
      : new URLSearchParams(params).toString();
  const query = search ? `?${search}` : '';

  return `${pathname}${query}`;
};

export function downloadLink(
  url: string,
  filename: string | undefined,
  newWindow?: boolean
): void {
  const a = document.createElement('a');
  a.href = url;

  if (newWindow) a.target = '_blank';

  if (filename !== undefined) a.download = filename;

  a.rel = 'noopener noreferrer nofollow';
  a.click();
}

// Login
export const getLoginPath = (): any =>
  buildUrl('/api/auth/login', {
    returnTo: location.pathname,
  });
export const redirectToLoginPath = (): void => {
  window.location.href = getLoginPath();
};

// Webshop
export const getHomePath = (): any => `/`;
export const getInventoryPath = (): any => `/inventory`;
export const getOrdersPath = (
  opts = {
    status: OrderStatus.Pending,
    scope: OrderScope.Me,
  }
): any =>
  buildUrl('/orders', {
    status: opts.status,
    scope: opts.scope,
  });

// Webshop - Orders
export const getOrdersActivePath = (): any =>
  getOrdersPath({ status: OrderStatus.Pending, scope: OrderScope.Me });
export const getOrdersPreviousPath = (): any =>
  getOrdersPath({ status: OrderStatus.Completed, scope: OrderScope.Me });
export const getManagerOrdersActivePath = (): any =>
  getOrdersPath({ status: OrderStatus.Pending, scope: OrderScope.Coworkers });
export const getManagerOrdersPreviousPath = (): any =>
  getOrdersPath({ status: OrderStatus.Completed, scope: OrderScope.Coworkers });
export const getOrderDetailPath = (order: string, scope: OrderScope): any =>
  `/orders/${order}?${new URLSearchParams({ scope }).toString()}`;
export const getOrderDetailActivityLogPath = (
  order: string,
  scope: OrderScope
): any => buildUrl(`/orders/${order}/activity-log`, { scope });

// Webshop - Shop
export const getShopPath = (
  location?: string,
  query: {
    category?: number;
  } = {}
): any => {
  const url = location ? `/shop/${location}` : `/shop`;

  return buildUrl(url, query as any);
};
export const getShopItemPath = (location: string, item: string): any =>
  `/shop/${location}/items/${item}`;

// Webshop - Checkout
export const getCheckoutPath = (): any => `/shop/checkout`;
export const getCheckoutConfirmationPath = (order: string): any => {
  const basePath = `${getCheckoutPath()}/confirmation`;
  const params = new URLSearchParams();

  if (order) params.set('order', order);

  return buildUrl(basePath, params);
};

// Webshop - FAQs
export const getFaqsPath = (): any => `/faqs`;

// Webshop - Wardrobe
export const getWardrobePath = (
  location?: string,
  query: {
    category?: number;
  } = {}
): any => {
  const url = location ? `/wardrobe/${location}` : `/wardrobe`;

  return buildUrl(url, query as any);
};
export const getWardrobeItemPath = (location: string, item: string): any =>
  `/wardrobe/${location}/items/${item}`;
export const getWardrobeCheckoutPath = (): any => `/wardrobe/checkout`;
export const getWardrobeCheckoutConfirmationPath = (order: string): any => {
  const basePath = `${getWardrobeCheckoutPath()}/confirmation`;
  const params = new URLSearchParams();

  if (order) params.set('order', order);

  return buildUrl(basePath, params);
};

// Admin
export const getAdminPath = (): any => `/admin`;
export const getAdminActivityLogPath = (): any => `/admin/activity-log`;
export const getAdminLocationDetailPath = (): any => `/admin/settings`;

// Admin - Tasks (orders)
export const getAdminTasksPath = (
  opts: {
    status: OrderStatus;
    cat?: CatQuery;
  } = {
    status: OrderStatus.Pending,
  }
): any => buildUrl('/admin/orders', opts);
export const getAdminPendingTasksPath = (cat: CatQuery): any =>
  getAdminTasksPath({ status: OrderStatus.Pending, cat });
export const getAdminCompletedTasksPath = (): any =>
  getAdminTasksPath({ status: OrderStatus.Completed });
export const getAdminOrderDetailPath = (order: string): any =>
  `/admin/orders/${order}`;
export const getAdminOrderDetailActivityLogPath = (order: string): any =>
  `${getAdminOrderDetailPath(order)}/activity-log`;

// Admin - Items
export const getAdminItemsPath = (): any => `/admin/items`;
export const getAdminItemDetailPath = (id: string): any => `/admin/items/${id}`;
export const getAdminItemDetailActivityLogPath = (item: string): any =>
  `${getAdminItemDetailPath(item)}/activity-log`;
export const getAdminItemDetailUsersPath = (item: string): any =>
  `${getAdminItemDetailPath(item)}/users`;
export const getAdminItemsGlobalPath = (): any => `/admin/items/global`;
export const getAdminWarehouseDetailPath = (id: string): any =>
  `/admin/warehouses/${id}`;

// Admin - Users
export const getAdminUsersPath = (
  opts: { type: UserTypeQuery } = {
    type: 'users',
  }
): any => buildUrl('/admin/users', opts);
export const getAdminUsersAdminsPath = (): any =>
  getAdminUsersPath({ type: 'administrators' });
export const getAdminUsersExternalPath = (): any =>
  getAdminUsersPath({ type: 'externalUsers' });

// Admin - Users - Detail
export const getAdminUserDetailPath = (email: string): any =>
  `/admin/users/${email}`;
export const getAdminUserDetailActivityLogPath = (email: string): any =>
  `${getAdminUserDetailPath(email)}/activity-log`;
export const getAdminUserDetailInventoryPath = (email: string): any =>
  `${getAdminUserDetailPath(email)}/inventory`;
export const getAdminUserDetailOrdersPath = (email: string): any =>
  `${getAdminUserDetailPath(email)}/orders`;

// Admin - Invitations and User Groups
export const getAdminInvitationDetailPath = (): any => `/admin/invitations`;
export const getAdminUserGroupDetailPath = (id: string): any =>
  `/admin/user-groups`;

// Superadmin
export const getSuperadminPath = (): any => `/superadmin`;
export const getSuperadminLocationsPath = (): any => `/superadmin/locations`;
